import { useMemo } from 'react';
import { Column } from 'react-table';
import { Button, Dropdown, MenuProps, Select } from 'antd';
import * as Icons from '@ant-design/icons';

import { StopPropagation } from '@shared/components/stop-propagation';
import { OverCell } from '@shared/components/cells';
import { nextTableColumns } from '@shared/modules/next-table/helpers/next-table-columns';
import { useBackgroundNavigate } from '@shared/hooks';
import { useModels } from '@modules/models';

import { IStatus } from '../../interfaces';
import { statusQueryKeys } from '../../utils';

export const useStatusesTableColumns = (): Column<IStatus>[] => {
  const navigate = useBackgroundNavigate();
  const { data: models = [] } = useModels();

  const actionsColumn = useMemo<Column<IStatus>>(
    () => ({
      ...nextTableColumns.actions,
      Cell: ({ row: { original } }: any) => {
        const items: MenuProps['items'] = [
          {
            key: 'edit',
            label: 'Düzəliş et',
            icon: <Icons.EditOutlined />,
            onClick: () => navigate(`/status/${original.id}/update`, { withBackground: true }),
          },
        ];

        return (
          <StopPropagation>
            <Dropdown menu={{ items }} trigger={['hover']}>
              <Button icon={<Icons.MoreOutlined />} size="small" />
            </Dropdown>
          </StopPropagation>
        );
      },
    }),
    [navigate],
  );

  const baseColumns = useMemo<Column<IStatus>[]>(
    () => [
      {
        ...nextTableColumns.small,
        accessor: (row) => row.id,
        id: statusQueryKeys.id,
        Header: 'Kod',
      },
      {
        accessor: (row) => row.name,
        id: statusQueryKeys.name,
        Header: 'Ad',
      },
      {
        accessor: (row) => row.nameEn,
        id: statusQueryKeys.nameEn,
        Header: 'Ingiliscə',
      },
      {
        accessor: (row) => row.model?.name,
        id: statusQueryKeys.modelId,
        Header: 'Model',
        Filter: ({ column: { filterValue, setFilter } }: any) => (
          <Select allowClear={true} style={{ width: '100%' }} onChange={setFilter} value={filterValue}>
            {models.map((elem) => (
              <Select.Option key={elem.id} value={elem.id.toString()}>
                {elem.name}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      {
        accessor: (row) => row.description,
        id: statusQueryKeys.description,
        Header: 'Açıqlama',
        Cell: OverCell,
      },
      {
        ...nextTableColumns.date,
        accessor: (row) => row.createdAt,
        id: statusQueryKeys.createdAt,
        Header: 'Yaradılıb',
      },
    ],
    [models],
  );

  return useMemo(() => [actionsColumn, ...baseColumns], [actionsColumn, baseColumns]);
};
